import { useState } from "react";
import { Search, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { useNewsStore } from "../../lib/newsStore";
import { MODEL_LABELS, KIND_LABELS } from "../../lib/newsApi";

const sel = "rounded-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700";

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-600">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-sky-900" data-testid={testId} />{label}
  </label>
);

export const NewsFilters = ({ meta, filters, setFilters }) => {
  const { saveSearch } = useNewsStore();
  const [q, setQ] = useState(filters.q || "");
  const set = (k) => (e) => setFilters({ ...filters, [k]: e?.target ? e.target.value : e });
  const save = () => {
    const name = window.prompt("Name this search", filters.q || filters.topic || "My search");
    if (name) { saveSearch(name, filters); toast.success("Search saved"); }
  };
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="news-filters">
      <form onSubmit={(e) => { e.preventDefault(); setFilters({ ...filters, q }); }} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search headlines and summaries…" maxLength={120}
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm" data-testid="news-search-input" />
        </div>
        <button type="submit" className="rounded-full bg-sky-900 px-5 text-sm font-medium text-white hover:bg-sky-800" data-testid="news-search-btn">Search</button>
        <button type="button" onClick={save} aria-label="Save search" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 text-sm text-slate-700 hover:border-sky-300 sm:px-4" data-testid="news-save-search-btn"><BookmarkPlus size={15} /><span className="hidden sm:inline">Save</span></button>
      </form>
      <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        <select value={filters.topic || ""} onChange={set("topic")} className={sel} data-testid="news-filter-topic" aria-label="Topic">
          <option value="">All topics</option>{meta?.categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={filters.province || ""} onChange={(e) => setFilters({ ...filters, province: e.target.value, city: "" })} className={sel} data-testid="news-filter-province" aria-label="Province">
          <option value="">All regions</option>{meta && Object.entries(meta.provinces).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
        </select>
        <select value={filters.city || ""} onChange={set("city")} className={sel} data-testid="news-filter-city" aria-label="Municipality">
          <option value="">All municipalities</option>{meta?.cities.filter((c) => !filters.province || c.province === filters.province).map((c) => <option key={c.city} value={c.city}>{c.city} ({c.count})</option>)}
        </select>
        <select value={filters.side || ""} onChange={set("side")} className={sel} data-testid="news-filter-side" aria-label="Source type">
          <option value="">Mainstream + independent</option><option value="mainstream">Mainstream only</option><option value="independent">Independent only</option>
        </select>
        <select value={filters.ownership || ""} onChange={set("ownership")} className={sel} data-testid="news-filter-ownership" aria-label="Ownership model">
          <option value="">All ownership models</option>{meta?.ownership_models.map((m) => <option key={m} value={m}>{MODEL_LABELS[m]}</option>)}
        </select>
        <select value={filters.source || ""} onChange={set("source")} className={sel} data-testid="news-filter-source" aria-label="Source">
          <option value="">All sources</option>{meta?.sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filters.kind || ""} onChange={set("kind")} className={sel} data-testid="news-filter-kind" aria-label="Content type">
          <option value="">All formats</option>{Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={filters.hours || 168} onChange={(e) => setFilters({ ...filters, hours: Number(e.target.value) })} className={sel} data-testid="news-filter-hours" aria-label="Time range">
          <option value={24}>Last 24 hours</option><option value={72}>Last 3 days</option><option value={168}>Last 7 days</option><option value={720}>Last 30 days</option>
        </select>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        <div className="flex rounded-full border border-slate-200 p-1 text-xs" role="tablist">
          {[["relevance", "Most relevant"], ["latest", "Latest"]].map(([v, l]) => (
            <button key={v} role="tab" aria-selected={filters.sort === v} onClick={() => setFilters({ ...filters, sort: v })} data-testid={`news-sort-${v}`}
              className={`rounded-full px-3 py-1 ${filters.sort === v ? "bg-sky-900 text-white" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <Toggle label="Group duplicate stories" checked={filters.dedupe} onChange={set("dedupe")} testId="news-toggle-dedupe" />
        <Toggle label="Balance mainstream / independent" checked={filters.balanced} onChange={set("balanced")} testId="news-toggle-balanced" />
        <Toggle label="Indigenous coverage" checked={filters.indigenous} onChange={set("indigenous")} testId="news-toggle-indigenous" />
      </div>
    </div>
  );
};
